import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SuchAuswahl from "@/components/ui/such-auswahl";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { TrendingUp, AlertTriangle, Info, Check, X, Trash2 } from "lucide-react";
import {
    mietverlauf, pruefeMietanpassung, createMietanpassung, updateMietanpassung,
    deleteMietanpassung, nachberechnung,
} from "@/lib/api";
import { euro, germanDate } from "@/lib/format";
import { toast } from "sonner";

// Der Anlass entscheidet über mehr als die Beschriftung: nur bei der
// Vergleichsmiete gelten Zustimmungsfrist, Sperrfrist und Kappungsgrenze
// (§ 558 BGB). Eine Staffel steht im Vertrag und ist sofort wirksam.
const ANLASS_HILFE = {
    vergleichsmiete: "Zustimmung des Mieters nötig. Sperrfrist, Kappungsgrenze und "
                   + "Zustimmungsfrist werden geprüft.",
    staffel: "Im Vertrag vereinbart — sofort wirksam, keine Zustimmung nötig. "
           + "Mehrere Stufen lassen sich in einem Zug erfassen.",
    index: "Folgt dem Verbraucherpreisindex. Keine Zustimmung, keine Kappungsgrenze.",
    modernisierung: "Umlage nach Modernisierung. Eigene Regeln, hier nicht geprüft.",
    nebenkosten: "Nur die Vorauszahlungen ändern sich.",
    vereinbarung: "Einvernehmlich zwischen den Parteien.",
};

// Kurze Namen fuer die Auswahl: die vollen Bezeichnungen des Servers
// ("Anpassung an die ortsübliche Vergleichsmiete (§ 558 BGB)") sind 60 px
// breiter als das Feld. Im Verlauf steht weiter die lange Fassung -- dort ist
// Platz, und dort zaehlt die Genauigkeit.
const ANLASS_KURZ = {
    vergleichsmiete: "Vergleichsmiete (§ 558)",
    staffel: "Staffel (§ 557a)",
    index: "Index (§ 557b)",
    modernisierung: "Modernisierung (§ 559)",
    nebenkosten: "Nebenkosten (§ 560)",
    vereinbarung: "Vereinbarung",
};

const STATUS_TON = {
    wirksam: "text-emerald-700 bg-emerald-50 border-emerald-200",
    geplant: "text-amber-700 bg-amber-50 border-amber-200",
    abgelehnt: "text-rose-700 bg-rose-50 border-rose-200",
    zurueckgezogen: "text-gray-500 bg-gray-50 border-gray-200",
};

const heuteMonat = () => new Date().toISOString().slice(0, 7);
const monatPlus = (m, n) => {
    const [j, mo] = m.split("-").map(Number);
    const g = j * 12 + (mo - 1) + n;
    return `${String(Math.floor(g / 12)).padStart(4, "0")}-${String((g % 12) + 1).padStart(2, "0")}`;
};

const HinweisZeile = ({ h }) => {
    const Icon = h.art === "warnung" || h.art === "fehler" ? AlertTriangle : Info;
    const ton = h.art === "warnung" || h.art === "fehler"
        ? "text-amber-800 bg-amber-50 border-amber-200"
        : "text-slate-600 bg-gray-50 border-gray-200";
    return (
        <div className={`flex items-start gap-2 rounded border px-2.5 py-2 text-[11px] ${ton}`}>
            <Icon size={13} className="mt-0.5 shrink-0" />
            <div>
                {h.regel && <div className="font-medium">{h.regel}</div>}
                <div className="leading-snug">{h.text}</div>
            </div>
        </div>
    );
};

// Wo die Kappungsgrenze steht -- unabhaengig davon, ob gerade jemand eine
// Erhoehung eintippt. Die Frage "wie viel Luft habe ich noch" kommt vor der
// Eingabe, nicht danach; die Hinweise im Formular kaemen dafuer zu spaet.
const Kappungsstand = ({ k }) => {
    if (!k) return null;
    const ohneBasis = !k.basis;
    return (
        <div className="rounded-md border border-gray-200 px-3 py-2.5 text-sm space-y-1.5"
             data-testid="kappungsstand">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-gray-500">
                Kappungsgrenze · {k.regel}
            </div>
            {ohneBasis ? (
                <div className="text-xs text-gray-500">
                    Für {k.basis_monat} ist keine Miete bekannt — vor dem Einzug gab es
                    hier nichts zu vergleichen. Die Kappungsgrenze greift erst, wenn drei
                    Jahre Mietverhältnis vorliegen.
                </div>
            ) : (
                <>
                    <div className="text-xs text-gray-600">
                        Vor drei Jahren ({k.basis_monat}): <b className="tabular-nums">{euro(k.basis)}</b>
                        {" · "}heute ({k.stichmonat}): <b className="tabular-nums">{euro(k.aktuell)}</b>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        {["normal", "angespannt"].map((art) => {
                            const s = k[art];
                            if (!s?.grenze) return null;
                            const eng = s.spielraum <= 0;
                            return (
                                <div key={art}
                                     className={`rounded border px-2 py-1.5 ${eng
                                         ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}
                                     data-testid={`kappung-${art}`}>
                                    <div className="text-[10px] uppercase tracking-wider text-gray-500">
                                        {s.prozent} % {art === "angespannt" ? "· angespannter Markt" : "· Normalfall"}
                                    </div>
                                    <div className="tabular-nums text-sm text-slate-900">
                                        bis {euro(s.grenze)}
                                    </div>
                                    <div className={`text-[11px] tabular-nums ${eng ? "text-amber-800" : "text-emerald-800"}`}>
                                        {eng
                                            ? `${euro(-s.spielraum)} darüber`
                                            : `noch ${euro(s.spielraum)} Luft`}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
            {k.sperrfrist_bis && (
                <div className="text-[11px] text-gray-500">
                    Letzte Anpassung {k.letzte_anpassung?.gilt_ab} · Jahressperrfrist
                    (§ 558 Abs. 1 BGB) bis <b>{k.sperrfrist_bis}</b>.
                </div>
            )}
        </div>
    );
};


// Eine rueckwirkende Anpassung laesst die schon erzeugten Monate alt stehen --
// das Soll ist eingefroren. Genau das war am 10.09.2026 der Fehler: Miete ab
// Maerz erhoeht, August forderte weiter 800 EUR, die Zahlung war 864.
const Nachberechnung = ({ stand, busy, onNachrechnen }) => {
    if (!stand) return null;
    const offen = stand.anzupassen || [];
    const hand = stand.von_hand_geaendert || [];
    // Ein Zuordnungsvorschlag ist aus dem Soll abgeleitet, das beim Lauf
    // galt. Ändert sich das Soll, steht er veraltet da und sieht aus wie
    // eine Teilzahlung.
    const zu = stand.zuordnungen || [];
    if (offen.length === 0 && hand.length === 0 && zu.length === 0) return null;
    return (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm space-y-2"
             data-testid="nachberechnung">
            {offen.length > 0 && (
                <>
                    <div className="flex items-start gap-2">
                        <AlertTriangle size={14} className="text-amber-700 mt-0.5 shrink-0" />
                        <div className="text-amber-900">
                            <b>{offen.length} bereits erzeugte{offen.length === 1 ? "r" : ""} Monat
                            {offen.length === 1 ? "" : "e"} rechnen noch mit dem alten Betrag.</b>
                            {" "}Zusammen {euro(stand.differenz_summe)} Unterschied — das Soll wird beim
                            Erzeugen eingefroren und ändert sich nicht von selbst.
                        </div>
                    </div>
                    <div className="text-[11px] text-amber-800 space-y-0.5 pl-6">
                        {offen.slice(0, 6).map((e) => (
                            <div key={e.charge_id}>
                                {e.period}: {euro(e.alt)} → <b>{euro(e.neu)}</b> ({euro(e.differenz)})
                            </div>
                        ))}
                        {offen.length > 6 && <div>… und {offen.length - 6} weitere</div>}
                    </div>
                </>
            )}
            {zu.length > 0 && (
                <div className={offen.length > 0 ? "border-t border-amber-200 pt-2" : ""}>
                    <div className="flex items-start gap-2">
                        <AlertTriangle size={14} className="text-amber-700 mt-0.5 shrink-0" />
                        <div className="text-amber-900">
                            <b>{zu.length === 1
                                ? "1 Zuordnungsvorschlag rechnet noch mit dem alten Soll."
                                : `${zu.length} Zuordnungsvorschläge rechnen noch mit dem alten Soll.`}</b>{" "}
                            Die Zahlung deckt mehr ab, als der Vorschlag zuweist.
                        </div>
                    </div>
                    <div className="text-[11px] text-amber-800 space-y-0.5 pl-6 mt-1">
                        {zu.slice(0, 6).map((e) => (
                            <div key={e.allocation_id}>
                                {e.period}: {euro(e.alt)} → <b>{euro(e.neu)}</b>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {(offen.length > 0 || zu.length > 0) && (
                <Button size="sm" className="h-8 bg-amber-700 hover:bg-amber-800"
                        disabled={busy} onClick={onNachrechnen}
                        data-testid="nachberechnung-anwenden">
                    {offen.length > 0
                        ? `${offen.length} Monat${offen.length === 1 ? "" : "e"} nachberechnen`
                        : `${zu.length} ${zu.length === 1 ? "Vorschlag" : "Vorschläge"} nachziehen`}
                </Button>
            )}
            {hand.length > 0 && (
                <div className="text-[11px] text-amber-800 border-t border-amber-200 pt-2">
                    {hand.length} Position{hand.length === 1 ? "" : "en"} bleib
                    {hand.length === 1 ? "t" : "en"} unangetastet, weil der Betrag nach der
                    Erzeugung von Hand geändert wurde:{" "}
                    {hand.slice(0, 4).map((e) => `${e.period} (${euro(e.alt)})`).join(", ")}
                    {hand.length > 4 ? " …" : ""}
                </div>
            )}
        </div>
    );
};


const MietanpassungDialog = ({ mieter, open, onOpenChange, onDone, gesperrt, grund }) => {
    const [daten, setDaten] = useState(null);
    const [busy, setBusy] = useState(false);
    const [anlass, setAnlass] = useState("vergleichsmiete");
    const [giltAb, setGiltAb] = useState(monatPlus(heuteMonat(), 3));
    const [mietart, setMietart] = useState("kalt_nk");
    const [betrag, setBetrag] = useState({ kaltmiete: "", nebenkosten: "", heizkosten: "", pauschalmiete: "" });
    const [schreibenAm, setSchreibenAm] = useState("");
    const [angespannt, setAngespannt] = useState(false);
    const [notiz, setNotiz] = useState("");
    // Staffel: statt fünfmal denselben Dialog auszufüllen, werden die Stufen
    // aus Anzahl, Abstand und Schritt erzeugt und bleiben danach änderbar.
    const [stufenAnzahl, setStufenAnzahl] = useState(3);
    const [abstand, setAbstand] = useState(12);
    const [schritt, setSchritt] = useState(20);
    const [pruefung, setPruefung] = useState(null);
    // Stand der Nachberechnung: welche schon erzeugten Monate rechnen noch
    // mit einem Betrag, den es so nicht mehr gibt.
    const [nachstand, setNachstand] = useState(null);

    const laden = useCallback(async () => {
        if (!mieter?.id) return;
        const v = await mietverlauf(mieter.id);
        setDaten(v);
        // Trockenlauf, nichts wird geschrieben: nur die Frage, ob alte
        // Monate zur heutigen Miete passen.
        nachberechnung({ tenant_id: mieter.id }, false)
            .then(setNachstand).catch(() => setNachstand(null));
        const jetzt = v.heute || v.vertrag;
        setMietart(jetzt.mietart || "kalt_nk");
        setBetrag({
            kaltmiete: jetzt.kaltmiete ?? "",
            nebenkosten: jetzt.nebenkosten ?? "",
            heizkosten: jetzt.heizkosten ?? "",
            pauschalmiete: jetzt.pauschalmiete ?? "",
        });
    }, [mieter]);

    useEffect(() => { if (open) { setPruefung(null); laden(); } }, [open, laden]);

    const zahl = (v) => (v === "" || v === null ? 0 : Number(v));
    const neueSumme = mietart === "pauschal"
        ? zahl(betrag.pauschalmiete)
        : zahl(betrag.kaltmiete) + zahl(betrag.nebenkosten) + zahl(betrag.heizkosten);

    // Die Prüfung läuft gegen den Server, nicht im Browser: die Regeln stehen
    // an einer Stelle, und die ist das Backend. Eine zweite Rechnung hier wäre
    // die zweite Wahrheit.
    const pruefen = useCallback(async () => {
        if (!mieter?.id || !giltAb) return;
        try {
            setPruefung(await pruefeMietanpassung({
                tenant_id: mieter.id, gilt_ab: giltAb, mietart, anlass,
                kaltmiete: zahl(betrag.kaltmiete), nebenkosten: zahl(betrag.nebenkosten),
                heizkosten: zahl(betrag.heizkosten), pauschalmiete: zahl(betrag.pauschalmiete),
                schreiben_am: schreibenAm || undefined, angespannter_markt: angespannt,
            }));
        } catch { setPruefung(null); }
    }, [mieter, giltAb, mietart, anlass, betrag, schreibenAm, angespannt]);

    useEffect(() => {
        if (!open) return;
        const id = setTimeout(pruefen, 400);   // nicht bei jedem Tastendruck
        return () => clearTimeout(id);
    }, [open, pruefen]);

    const stufen = () => {
        const basis = {
            mietart,
            kaltmiete: mietart === "pauschal" ? null : zahl(betrag.kaltmiete),
            nebenkosten: mietart === "pauschal" ? null : zahl(betrag.nebenkosten),
            heizkosten: mietart === "pauschal" ? null : zahl(betrag.heizkosten),
            pauschalmiete: mietart === "pauschal" ? zahl(betrag.pauschalmiete) : null,
        };
        if (anlass !== "staffel" || stufenAnzahl <= 1) return [{ gilt_ab: giltAb, ...basis }];
        return Array.from({ length: stufenAnzahl }, (_, i) => ({
            gilt_ab: monatPlus(giltAb, i * abstand),
            ...basis,
            ...(mietart === "pauschal"
                ? { pauschalmiete: zahl(betrag.pauschalmiete) + i * zahl(schritt) }
                : { kaltmiete: zahl(betrag.kaltmiete) + i * zahl(schritt) }),
        }));
    };

    const speichern = async () => {
        setBusy(true);
        try {
            const r = await createMietanpassung({
                tenant_id: mieter.id, anlass, schreiben_am: schreibenAm || null,
                angespannter_markt: angespannt, notiz: notiz || null, stufen: stufen(),
            });
            toast.success(`${r.angelegt} Anpassung${r.angelegt === 1 ? "" : "en"} angelegt`, {
                description: anlass === "staffel"
                    ? "Die Stufen sind sofort wirksam — sie stehen im Vertrag."
                    : "Noch nicht wirksam: erst mit der Zustimmung des Mieters.",
            });
            setNotiz("");
            await laden();
            onDone?.();
        } catch (e) {
            toast.error("Nicht angelegt", { description: e?.response?.data?.detail || "" });
        } finally { setBusy(false); }
    };

    const statusSetzen = async (a, patch, text) => {
        setBusy(true);
        try {
            await updateMietanpassung(a.id, patch);
            toast.success(text);
            await laden(); onDone?.();
        } finally { setBusy(false); }
    };

    const entfernen = async (a) => {
        setBusy(true);
        try {
            await deleteMietanpassung(a.id);
            toast.success("Anpassung gelöscht");
            await laden(); onDone?.();
        } finally { setBusy(false); }
    };

    if (!mieter) return null;
    const jetzt = daten?.heute || daten?.vertrag;
    const vorschau = stufen();

    const nachrechnen = async () => {
        setBusy(true);
        try {
            const r = await nachberechnung({ tenant_id: mieter.id }, true);
            const teile = [];
            if (r.anzupassen.length) teile.push(`${r.anzupassen.length} Soll-Position(en) nachberechnet`);
            if (r.zuordnungen?.length) teile.push(`${r.zuordnungen.length} Zuordnung(en) nachgezogen`);
            toast.success(teile.join(", ") || "Nichts zu tun",
                          { description: r.differenz_summe
                              ? `Unterschied ${r.differenz_summe.toFixed(2)} €`
                              : "Die Vorschläge decken jetzt das volle Soll." });
            await laden();
            onDone?.();
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Miete anpassen — {mieter.full_name}</DialogTitle>
                    <DialogDescription>
                        Eine Anpassung gilt <b>ab einem Monat</b>. Schon erzeugte Soll-Positionen
                        bleiben, wie sie sind; alles, was danach erzeugt wird, rechnet mit dem
                        Betrag, der in dem Monat gilt. Fristen und Kappungsgrenze sind Hinweise,
                        keine Sperren — und keine Rechtsberatung.
                    </DialogDescription>
                </DialogHeader>

                {/* --- Was heute gilt --- */}
                <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm flex items-center justify-between">
                    <span className="text-gray-500">Aktuell</span>
                    <span className="font-semibold tabular-nums" data-testid="anpassung-aktuell">
                        {jetzt ? euro(jetzt.soll_total) : "—"}
                        {jetzt?.quelle && jetzt.quelle !== "mietverhaeltnis" && (
                            <span className="ml-2 text-[11px] font-normal text-gray-500">
                                seit {jetzt.gilt_ab}
                            </span>
                        )}
                    </span>
                </div>

                <Nachberechnung stand={nachstand} busy={busy} onNachrechnen={nachrechnen} />

                <Kappungsstand k={daten?.kappung} />

                {/* --- Verlauf --- */}
                {/* Der Vertrag ist der Anfang der Geschichte. Ohne ihn beginnt
                    der Verlauf mitten im Satz -- und man sieht nicht, wovon die
                    erste Erhöhung ausging. */}
                <div className="border border-gray-200 rounded-md divide-y divide-gray-100">
                    {daten?.vertrag && (
                        <div className="px-3 py-2 text-sm flex items-center gap-2 flex-wrap bg-gray-50/60"
                             data-testid="verlauf-vertrag">
                            <span className="font-mono text-xs w-16">{daten.vertrag.gilt_ab || "—"}</span>
                            <span className="font-semibold tabular-nums w-24">
                                {euro(daten.vertrag.soll_total)}
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded border text-gray-500 bg-white border-gray-200">Vertrag</span>
                            <span className="text-[11px] text-gray-500 flex-1">
                                Betrag am Mietverhältnis — gilt, solange keine Anpassung greift
                            </span>
                        </div>
                    )}
                        {(daten?.anpassungen || []).map((a) => {
                            const summe = a.mietart === "pauschal"
                                ? a.pauschalmiete
                                : (a.kaltmiete || 0) + (a.nebenkosten || 0) + (a.heizkosten || 0);
                            const warnungen = (a.hinweise || []).filter((h) => h.art === "warnung");
                            return (
                                <div key={a.id} className="px-3 py-2 text-sm flex items-center gap-2 flex-wrap"
                                     data-testid={`anpassung-${a.id}`}>
                                    <span className="font-mono text-xs w-16">{a.gilt_ab}</span>
                                    <span className="font-semibold tabular-nums w-24">{euro(summe)}</span>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_TON[a.status]}`}>
                                        {a.status}
                                    </span>
                                    <span className="text-[11px] text-gray-500 flex-1 min-w-[8rem] truncate"
                                          title={daten.anlaesse?.[a.anlass]}>
                                        {daten.anlaesse?.[a.anlass] || a.anlass}
                                        {warnungen.length > 0 && (
                                            <span className="ml-1 text-amber-700">
                                                · {warnungen.length} Hinweis{warnungen.length === 1 ? "" : "e"}
                                            </span>
                                        )}
                                    </span>
                                    {!gesperrt && a.status === "geplant" && (
                                        <Button variant="ghost" size="sm" className="h-7 px-2 text-emerald-700"
                                                data-testid={`anpassung-zustimmen-${a.id}`}
                                                onClick={() => statusSetzen(a, { zustimmung_am: new Date().toISOString().slice(0, 10) },
                                                                            "Zustimmung eingetragen — die Anpassung ist wirksam")}
                                                title="Mieter hat zugestimmt">
                                            <Check size={13} />
                                        </Button>
                                    )}
                                    {!gesperrt && a.status !== "zurueckgezogen" && (
                                        <Button variant="ghost" size="sm" className="h-7 px-2 text-gray-500"
                                                data-testid={`anpassung-zurueck-${a.id}`}
                                                onClick={() => statusSetzen(a, { status: "zurueckgezogen" },
                                                                            "Zurückgezogen — zählt nicht mehr")}
                                                title="Zurückziehen — bleibt sichtbar, zählt aber nicht">
                                            <X size={13} />
                                        </Button>
                                    )}
                                    {!gesperrt && (
                                        <Button variant="ghost" size="sm" className="h-7 px-2 text-gray-400 hover:text-rose-700"
                                                data-testid={`anpassung-loeschen-${a.id}`}
                                                onClick={() => entfernen(a)}
                                                title="Löschen — nimmt der Miete ihre Geschichte">
                                            <Trash2 size={13} />
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                </div>

                {/* --- Neue Anpassung --- */}
                <div className="space-y-3 border-t border-gray-100 pt-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <Label>Anlass</Label>
                            <SuchAuswahl wert={anlass} onWaehlen={setAnlass}
                                optionen={[
                                    ...Object.keys(daten?.anlaesse || {}).map((k) => ({ wert: k, text: ANLASS_KURZ[k] || k })),
                                ]}
                                testId="anpassung-anlass"
                            />
                            <div className="mt-1 text-[11px] text-gray-500 leading-snug">
                                {ANLASS_HILFE[anlass]}
                            </div>
                        </div>
                        <div>
                            <Label>Gilt ab Monat</Label>
                            <Input type="month" value={giltAb} onChange={(e) => setGiltAb(e.target.value)}
                                   data-testid="anpassung-gilt-ab" />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                            <Label>Mietart</Label>
                            <SuchAuswahl wert={mietart} onWaehlen={setMietart}
                                optionen={[
                                    { wert: "kalt_nk", text: "kalt + NK" },
                                    { wert: "pauschal", text: "Pauschal" },
                                ]}
                                testId="anpassung-mietart"
                            />
                        </div>
                        {mietart === "pauschal" ? (
                            <div>
                                <Label>Pauschalmiete</Label>
                                <Input type="number" step="0.01" value={betrag.pauschalmiete}
                                       onChange={(e) => setBetrag({ ...betrag, pauschalmiete: e.target.value })}
                                       data-testid="anpassung-pauschalmiete" />
                            </div>
                        ) : (
                            <>
                                <div>
                                    <Label>Kaltmiete</Label>
                                    <Input type="number" step="0.01" value={betrag.kaltmiete}
                                           onChange={(e) => setBetrag({ ...betrag, kaltmiete: e.target.value })}
                                           data-testid="anpassung-kaltmiete" />
                                </div>
                                <div>
                                    <Label>Nebenkosten</Label>
                                    <Input type="number" step="0.01" value={betrag.nebenkosten}
                                           onChange={(e) => setBetrag({ ...betrag, nebenkosten: e.target.value })}
                                           data-testid="anpassung-nebenkosten" />
                                </div>
                                <div>
                                    <Label>Heizkosten</Label>
                                    <Input type="number" step="0.01" value={betrag.heizkosten}
                                           onChange={(e) => setBetrag({ ...betrag, heizkosten: e.target.value })}
                                           data-testid="anpassung-heizkosten" />
                                </div>
                            </>
                        )}
                    </div>

                    {anlass === "staffel" && (
                        <div className="grid grid-cols-3 gap-3 rounded-md bg-gray-50 border border-gray-200 p-3">
                            <div>
                                <Label>Stufen</Label>
                                <Input type="number" min="1" max="12" value={stufenAnzahl}
                                       onChange={(e) => setStufenAnzahl(Number(e.target.value))}
                                       data-testid="staffel-anzahl" />
                            </div>
                            <div>
                                <Label>Abstand (Monate)</Label>
                                <Input type="number" min="1" value={abstand}
                                       onChange={(e) => setAbstand(Number(e.target.value))}
                                       data-testid="staffel-abstand" />
                            </div>
                            <div>
                                <Label>Schritt (€)</Label>
                                <Input type="number" step="0.01" value={schritt}
                                       onChange={(e) => setSchritt(e.target.value)}
                                       data-testid="staffel-schritt" />
                            </div>
                        </div>
                    )}

                    {anlass === "vergleichsmiete" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <Label>Erhöhungsschreiben zugegangen am</Label>
                                <Input type="date" value={schreibenAm}
                                       onChange={(e) => setSchreibenAm(e.target.value)}
                                       data-testid="anpassung-schreiben-am" />
                                <div className="mt-1 text-[11px] text-gray-500">
                                    Daraus ergibt sich die Zustimmungsfrist (§ 558b Abs. 2 BGB).
                                </div>
                            </div>
                            <div className="flex items-start gap-2 pt-6">
                                <input type="checkbox" checked={angespannt} className="mt-1"
                                       onChange={(e) => setAngespannt(e.target.checked)}
                                       data-testid="anpassung-angespannt" />
                                <Label className="font-normal leading-snug">
                                    Angespannter Wohnungsmarkt — Kappungsgrenze 15 % statt 20 %
                                </Label>
                            </div>
                        </div>
                    )}

                    <div>
                        <Label>Notiz</Label>
                        <Input value={notiz} onChange={(e) => setNotiz(e.target.value)}
                               placeholder="z. B. Vertrag § 4, Mietspiegel 2026"
                               data-testid="anpassung-notiz" />
                    </div>

                    {/* --- Vorschau der Stufen --- */}
                    {vorschau.length > 1 && (
                        <div className="text-[11px] text-gray-600" data-testid="staffel-vorschau">
                            <div className="font-medium mb-1">{vorschau.length} Stufen:</div>
                            <div className="flex flex-wrap gap-1.5">
                                {vorschau.map((s) => (
                                    <span key={s.gilt_ab} className="rounded border border-gray-200 bg-white px-1.5 py-0.5 tabular-nums">
                                        {s.gilt_ab} · {euro(s.mietart === "pauschal" ? s.pauschalmiete
                                            : (s.kaltmiete || 0) + (s.nebenkosten || 0) + (s.heizkosten || 0))}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* --- Hinweise vom Server --- */}
                    {pruefung && (
                        <div className="space-y-1.5" data-testid="anpassung-hinweise">
                            <div className="text-[11px] text-gray-500">
                                {euro(pruefung.bisher.soll_total)} → <b>{euro(neueSumme)}</b>
                                {pruefung.bisher.soll_total > 0 && (
                                    <> · {((neueSumme / pruefung.bisher.soll_total - 1) * 100).toFixed(1)} %</>
                                )}
                            </div>
                            {pruefung.hinweise.map((h, i) => <HinweisZeile key={i} h={h} />)}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button onClick={speichern} disabled={busy || gesperrt || neueSumme <= 0}
                            title={grund} className="bg-slate-900 hover:bg-slate-800"
                            data-testid="anpassung-speichern">
                        <TrendingUp size={14} className="mr-1.5" />
                        {vorschau.length > 1 ? `${vorschau.length} Stufen anlegen` : "Anpassung anlegen"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default MietanpassungDialog;
